import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { FilterOption, ProjectSummary } from '@/types'

import { useMemo } from 'react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { useQueryFilter } from '@/lib/use-query-filter'
import { padNumber } from '@/lib/format'
import { breadcrumbJsonLd } from '@/lib/structured-data'
import { getAllProjects, getFeaturedProjects, getProjectFilters } from '@/lib/content/projects'
import MainContainer from '@/components/generals/main-container'
import PageHeader from '@/components/generals/page-header'
import SectionHeading from '@/components/generals/section-heading'
import ProjectFeature from '@/components/projects/project-feature'
import ProjectListItem from '@/components/projects/project-list-item'
import FilterBar from '@/components/elements/filter-bar'
import EmptyState from '@/components/elements/empty-state'
import Button from '@/components/elements/button'

interface ProjectsPageProps {
    projects: Array<ProjectSummary>
    featured: Array<ProjectSummary>
    filters: Array<FilterOption>
}

const ProjectsPage: FC<ProjectsPageProps> = ({ projects, featured, filters }): ReactNode => {
    const [category, setCategory] = useQueryFilter('category', filters.map((filter) => filter.value))

    const visible = useMemo(() => {
        return category === 'all' ? projects : projects.filter((project) => project.category === category)
    }, [projects, category])

    const activeLabel = filters.find((filter) => filter.value === category)?.label

    return (
        <Layout>
            <Seo
                title='Work'
                path='/projects'
                description="Case studies in software engineering, healthcare systems, AI applications, self-hosted infrastructure and automation, each with its architecture, trade-offs and lessons."
                jsonLd={breadcrumbJsonLd([
                    { name: 'Home', path: '/' },
                    { name: 'Work', path: '/projects' }
                ])}
            />

            <PageHeader
                eyebrow='Work / Projects'
                title={<>Projects built to be useful, <em className='text-brand'>documented</em> in the open.</>}
                intro={
                    <p>
                        Case studies from healthcare software, AI applications, self-hosted infrastructure, developer
                        tools and automation. Each one covers the problem, the architecture, the trade-offs and what was learned.
                    </p>
                }
                aside={
                    <p className='font-mono text-caption text-muted-foreground'>
                        <span className='block font-heading text-[3.5rem] leading-none tracking-[-0.03em] text-foreground not-italic'>
                            {padNumber(projects.length)}
                        </span>
                        <span className='mt-2 block uppercase tracking-label'>Entries in the index</span>
                    </p>
                }
            />

            <MainContainer as='section' className='pb-20 md:pb-28' id='featured'>
                <SectionHeading
                    index='01'
                    label='Featured'
                    title='Selected case studies'
                    description='The projects that best show how the work is approached, from the first constraint to the last lesson.'
                />

                <div className='mt-12 flex flex-col gap-16 md:mt-16 md:gap-24'>
                    {featured[0] && (
                        <ProjectFeature project={featured[0]} index={1} layout='wide' headingLevel='h3' priority />
                    )}

                    {featured.length > 1 && (
                        <div className='grid grid-cols-1 gap-16 md:grid-cols-12 md:gap-x-8'>
                            {featured.slice(1).map((project, index) => (
                                <ProjectFeature
                                    key={project.slug}
                                    project={project}
                                    index={index + 2}
                                    className={index === 0 ? 'md:col-span-5' : 'md:col-span-6 md:col-start-7 md:mt-24'}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </MainContainer>

            <MainContainer as='section' className='pb-24 md:pb-32' id='index'>
                <SectionHeading
                    index='02'
                    label='Index'
                    title='Every project, by category'
                />

                <div className='mt-10 flex flex-col gap-6 border-b border-border pb-6 md:flex-row md:items-center md:justify-between'>
                    <FilterBar
                        label='Filter projects by category'
                        options={filters}
                        value={category}
                        onChange={setCategory}
                        total={projects.length}
                    />

                    <p aria-live='polite' className='shrink-0 font-mono text-caption text-muted-foreground'>
                        {visible.length} of {projects.length} shown
                    </p>
                </div>

                {visible.length > 0 ? (
                    <ul aria-label={activeLabel ? `${activeLabel} projects` : 'All projects'}>
                        {visible.map((project) => (
                            <ProjectListItem key={project.slug} project={project} />
                        ))}
                    </ul>
                ) : (
                    <EmptyState
                        className='mt-10'
                        title='Nothing in this category yet'
                        description={`There are no ${activeLabel ?? 'matching'} projects in the index at the moment.`}
                        action={<Button variant='secondary' onClick={() => setCategory('all')}>Show all projects</Button>}
                    />
                )}
            </MainContainer>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ProjectsPageProps> = async () => {
    return {
        props: {
            projects: getAllProjects(),
            featured: getFeaturedProjects(3),
            filters: getProjectFilters()
        }
    }
}

export default ProjectsPage
